import React, { useContext, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Paper, Typography, Divider, TextField, Select, InputLabel, MenuItem, Autocomplete, IconButton, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useParams } from 'react-router';
import { useCreateDigitalBookingMutation, useGetSiteQueryQuery, useGetSiteDigitalBookingsQuery, useGetCampaignDigitalBookingsQuery, useDeleteDigitalBookingMutation } from '../../generated/graphql';
import { CampaignShortcutSelect } from "../../components/MaterialSelects/CampaignShortcutSelect";
import { DateRange } from 'react-date-range';
import { eachDayOfInterval, format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { differenceInCalendarDays } from 'date-fns';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { ToolbarContext } from '../../contexts/toolbar-context';
import { asMoney } from '../../utils';
import { useSnackbar } from "notistack";
import { start } from 'repl';
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

export function DigitalBookingEditor() {
    const params = useParams();
    const query = useGetSiteQueryQuery({ variables: { id: params.id } });
    const [deleteDigitalBooking] = useDeleteDigitalBookingMutation();
    const { data: digitalBookingsData, refetch: refetchDigitalBookings } = useGetSiteDigitalBookingsQuery({
        variables: { siteId: params.id },
        fetchPolicy: 'network-only'
    });
    const toolbarContext = useContext(ToolbarContext);
    const baseUrl = '/digital-bookings/';
    const [createDigitalBooking] = useCreateDigitalBookingMutation();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [campaignBookings, setCampaignBookings] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    const validationSchema = Yup.object().shape({
        campaignId: Yup.string().required('Campaign is required'),
        cost3p: Yup.string().when('$is3P', {
            is: true,
            then: Yup.string().required('Please enter the net cost'),
            otherwise: Yup.string(),
        }),
        agencyPercentage: Yup.number()
            .nullable()
            .when('costType', {
                is: 'agency',
                then: Yup.number()
                    .required('Gross Up Percentage is required')
                    .typeError('Gross Up Percentage must be a number'),
                otherwise: Yup.number().nullable(),
            }),
    });
    const [formState, setFormState] = useState({
        campaignId: '',
        costType: 'direct',
        cost3p: '',
        dateRange: {
            startDate: new Date(),
            endDate: new Date(),
        },
        disabledDates: [],
        slotCountDates: [],
        agencyPercentage: null
    });

    useEffect(() => {
        setFormState((prevState) => ({
            ...prevState,
            agencyPercentage: null,
        }));
    }, [query.data?.site]);

    const getTotalDays = () => {
        const { startDate, endDate } = formState.dateRange;
        const days = differenceInCalendarDays(endDate, startDate) + 1;
        return days > 0 ? days : 1;
    };

    const getPrice = () => {
        const { cost3p, costType, agencyPercentage } = formState;

        let netPrice = 0;
        let grossPrice = 0;

        if (query.data?.site?.is_3p && cost3p) {
            netPrice = parseFloat(cost3p) * 100;

            if (costType === 'agency' && agencyPercentage) {
                const percentageFactor = (100 - agencyPercentage) / 100;
                grossPrice = Math.round(netPrice / percentageFactor);
            } else {
                grossPrice = netPrice;
            }

            return { netPrice, grossPrice };
        }

        const days = getTotalDays();
        const pricingData = query.data?.site?.siteDigitalBookingPricing;

        if (!pricingData) {
            console.error("No pricing data found for non-3P site.");
            return { netPrice: 0, grossPrice: 0 };
        }

        const priceEntry = pricingData.find(entry => entry.days === days);

        if (!priceEntry) {
            console.error("No pricing found for the specified number of days.");
            return { netPrice: 0, grossPrice: 0 };
        }

        netPrice = priceEntry.net;

        if (costType === 'agency' && agencyPercentage) {
            const percentageFactor = (100 - agencyPercentage) / 100;
            grossPrice = Math.round(netPrice / percentageFactor);
        } else {
            grossPrice = netPrice;
        }

        return { netPrice, grossPrice };
    };

    const handleDateRangeChange = (ranges: any) => {
        const { startDate, endDate } = ranges.selection;
        const diffInDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1;

        const adjustedEndDate = diffInDays >= 15
            ? new Date(startDate.getTime() + 13 * 24 * 60 * 60 * 1000)
            : endDate;

        setFormState((prevState) => ({
            ...prevState,
            dateRange: { startDate, endDate: adjustedEndDate },
        }));

    };

    const handleRefreshDates = async () => {
        try {
            const { data } = await refetchDigitalBookings();
            if (data?.getSiteDigitalBookings) {
                const bookedDatesMap = {};
                const slotCountDates = {};

                data.getSiteDigitalBookings.forEach((booking) => {
                    if (booking.status === 'Booked') {
                        const currentBookingDates = eachDayOfInterval({
                            start: new Date(booking.start_date),
                            end: new Date(booking.end_date),
                        });

                        currentBookingDates.forEach((date) => {
                            const dateStr = format(date, 'yyyy-MM-dd');
                            bookedDatesMap[dateStr] = (bookedDatesMap[dateStr] || 0) + 1;
                        });
                    }
                });
                query.data?.site?.panels.forEach((panel) => {
                    const numberOfSlots = panel.number_of_slots;
                    Object.keys(bookedDatesMap).forEach((dateStr) => {
                        const bookedSlots = bookedDatesMap[dateStr];

                        if (bookedSlots >= numberOfSlots) {
                            slotCountDates[dateStr] = 'gray';
                        }
                    });
                });

                setFormState((prevState) => ({
                    ...prevState,
                    disabledDates: Object.keys(slotCountDates).filter(date => slotCountDates[date] === 'gray').map(date => new Date(date)),
                    slotCountDates,
                }));
            }
        } catch (error) {
            console.error("Error refreshing dates:", error);
        }
    };
    const handleChange = (event: any) => {
        const { name, value } = event.target;
        setFormState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };
    const handleSubmit = async () => {
        try {
            const is3P = query.data?.site?.is_3p;
            await validationSchema.validate(
                {
                    campaignId: formState.campaignId,
                    cost3p: formState.cost3p,
                    costType: formState.costType,
                    agencyPercentage: formState.agencyPercentage,
                },
                { context: { is3P } }
            );
            setFormErrors({})

            enqueueSnackbar('Saving digital booking...', { variant: 'info' });
            const { campaignId, costType, dateRange, cost3p, agencyPercentage } = formState;
            const { startDate, endDate } = dateRange;
            const days = getTotalDays();
            const regionId = query.data?.site?.region?.id;
            const { grossPrice } = getPrice();
            const unit_value_local = parseFloat(grossPrice || 0);
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            // Execute the mutation
            const { data } = await createDigitalBooking({
                variables: {
                    input: {
                        campaign_id: campaignId,
                        days: days,
                        cost_type: costType,
                        agency_discount: agencyPercentage,
                        start_date: formattedStartDate,
                        end_date: formattedEndDate,
                        region_id: regionId,
                        site_id: query.data?.site?.id,
                        region_type_id: query.data?.site?.region?.regionType?.id,
                        unit_value_local: unit_value_local,
                    },
                },
            });

            if (data && data.createDigitalBookings) {
                await refetchDigitalBookings();
                await refetchCampaignBookings();
                enqueueSnackbar('Booking saved successfully!', { variant: 'success' });
            }
        } catch (error) {
            if (error.name === 'ValidationError') {
                setFormErrors({ [error.path]: error.message });
                enqueueSnackbar(error.message, { variant: 'error' });
            } else {
                enqueueSnackbar('Error creating digital booking.', { variant: 'error' });
                console.error(error);
            }
        }
    };


    const buttons = [];
    buttons.push({
        type: 'button',
        icon: <AddIcon />,
        tooltip: 'Save',
        callback: handleSubmit,
        label: 'Save',
        style: 'primary',
    });
    toolbarContext.setSecondary(
        {
            primaryUrl: baseUrl,
            title: ('New Digital Booking'),
            secondary: buttons,
        });

    const { data: campaignData, refetch: refetchCampaignBookings } = useGetCampaignDigitalBookingsQuery({
        variables: { campaignId: formState.campaignId },
        fetchPolicy: 'network-only',
        skip: !formState.campaignId,
    });

    useEffect(() => {
        // Update campaignBookings state when campaignData changes
        if (campaignData?.getCampaignDigitalBookings) {
            setCampaignBookings(campaignData.getCampaignDigitalBookings);
        }
    }, [campaignData]);

    const handleDeleteBooking = async (bookingId) => {
        try {
            const confirmDelete = window.confirm("Are you sure you want to delete this booking?");
            if (!confirmDelete) return;

            const { data } = await deleteDigitalBooking({
                variables: {
                    input: {
                        deletes: [bookingId]
                    }
                },
            });

            if (data) {
                enqueueSnackbar('Booking deleted successfully!', { variant: 'success' });
                await refetchCampaignBookings();
            }
        } catch (error) {
            console.error("Error deleting booking:", error);
        }
    };

    return (
        <React.Fragment>
            <>
                <Accordion defaultExpanded={true}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Create Booking - {query.data?.site?.name}
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <CampaignShortcutSelect
                                    valueIsId={true}
                                    name="campaignId"
                                    label="Campaign"
                                    value={formState.campaignId}
                                    onChange={handleChange}
                                    error={!!formErrors.campaignId}
                                    helperText={formErrors.campaignId}
                                />
                                {query.data?.site?.is_3p && (
                                    <TextField
                                        name="cost3p"
                                        label="Enter Net Sales Price To Client"
                                        value={formState.cost3p}
                                        onChange={handleChange}
                                        fullWidth
                                        error={!!formErrors.cost3p}
                                    />
                                )}
                                <FormControl component="fieldset" sx={{ marginTop: 2 }}>
                                    <FormLabel component="legend">Sale Type</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="cost-type-radio-group"
                                        name="costType"
                                        value={formState.costType}
                                        onChange={handleChange}
                                    >
                                        <FormControlLabel value="direct" control={<Radio />} label="Direct" />
                                        <FormControlLabel value="agency" control={<Radio />} label="Agency" />
                                    </RadioGroup>
                                </FormControl>

                                {formState.costType === 'agency' && (
                                    <FormControl fullWidth sx={{ marginTop: 2 }}>
                                        <Autocomplete
                                            id="gross-percentage-autocomplete"
                                            options={[10, 15, 20]}
                                            value={formState.agencyPercentage}
                                            getOptionLabel={(option) => `${option}%`}
                                            onChange={(event, newValue) => {
                                                setFormState((prevState) => ({
                                                    ...prevState,
                                                    agencyPercentage: newValue || null,
                                                }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Gross Up Percentage (should match campaign discount)"
                                                    placeholder="Select or type percentage"
                                                    error={!!formErrors.agencyPercentage}
                                                    helperText={formErrors.agencyPercentage}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                )}


                                {/* Invoice Section */}
                                <Grid item xs={12} sx={{ marginTop: 3 }}>
                                    <Paper elevation={3} sx={{ padding: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Booking Summary
                                        </Typography>
                                        <Divider sx={{ marginBottom: 2 }} />
                                        <Box display="flex" justifyContent="space-between" sx={{ marginTop: 1 }}>
                                            <Typography variant="subtitle1">Total Days Selected:</Typography>
                                            <Typography variant="subtitle1">{getTotalDays()}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between" sx={{ marginTop: 1 }}>
                                            <Typography variant="subtitle1">Cost Type:</Typography>
                                            <Typography variant="subtitle1">{formState.costType === 'agency' ? 'Agency' : 'Direct'}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between" sx={{ marginTop: 1 }}>
                                            <Typography variant="subtitle1">Price Net:</Typography>
                                            <Typography variant="subtitle1">£{asMoney(getPrice().netPrice)}</Typography>
                                        </Box>
                                        {formState.costType === "agency" && (
                                            <Box display="flex" justifyContent="space-between" sx={{ marginTop: 1 }}>
                                                <Typography variant="subtitle1">Price Gross:</Typography>
                                                <Typography variant="subtitle1">£{asMoney(getPrice().grossPrice)}</Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="100%"
                                    gap={2}
                                    flexDirection="column"
                                >
                                    <DateRange
                                        ranges={[
                                            {
                                                startDate: formState.dateRange.startDate,
                                                endDate: formState.dateRange.endDate,
                                                key: 'selection',
                                            },
                                        ]}
                                        onChange={handleDateRangeChange}
                                        moveRangeOnFirstSelection={false}
                                        rangeColors={["#3f51b5"]}
                                        disabledDates={formState.disabledDates}
                                    />
                                    < Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={< RefreshIcon />}
                                        onClick={handleRefreshDates}
                                    >
                                        Refresh Dates For Latest Availability
                                    </Button>
                                </Box>
                            </Grid>

                        </Grid>
                    </AccordionDetails>
                </Accordion>
                <Accordion defaultExpanded={true}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Existing Bookings {campaignData?.getCampaignDigitalBookings?.[0]?.campaign?.job_reference || 'No Bookings Available'}
                    </AccordionSummary>
                    <AccordionDetails>
                        {campaignData?.getCampaignDigitalBookings?.length > 0 ? (
                            <Grid container spacing={2}>
                                {campaignData.getCampaignDigitalBookings.map((booking) => (
                                    <Grid item xs={12} sm={6} md={4} key={booking.id}>
                                        <Paper elevation={3} sx={{ padding: 2, position: "relative" }}>
                                            {/* Campaign Job Reference with Link */}
                                            <Typography variant="subtitle1" gutterBottom>
                                                Job Reference:{" "}
                                                <a
                                                    href={`/campaigns/${booking.campaign?.job_reference}/digital-bookings`}
                                                    rel="noopener noreferrer"
                                                    style={{ textDecoration: "none" }}
                                                >
                                                    {booking.campaign?.job_reference}
                                                </a>
                                            </Typography>

                                            {/* Site Name */}
                                            <Typography variant="body2">
                                                Site: {booking.site?.name}
                                            </Typography>

                                            {/* Delete Button */}
                                            <IconButton
                                                aria-label="delete"
                                                size="small"
                                                color="error"
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                }}
                                                onClick={() => handleDeleteBooking(booking.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Typography>No bookings available for this campaign.</Typography>
                        )}
                    </AccordionDetails>

                </Accordion>
            </>
        </React.Fragment>
    );
}
